import AbstractService, {
  InterfaceAuth,
  ServiceAuth,
  ModelUser
} from "./AbstractService";

/**
 * AuthSsoProvider
 */
export enum AuthSsoProvider {
  MICROSOFT = "microsoft"
}

/**
 * AuthService
 */
export default class AuthService extends AbstractService implements InterfaceAuth {
  /**
   * ServiceAuth
   */
  static service: ServiceAuth;

  /**
   * constructor
   */
  constructor() {
    super();

    AuthService.service = this.getApi().getServiceAuth();
  }

  /**
   * clearToken
   */
  clearToken(): void {
    AuthService.service.clearToken();
  }

  /**
   * clearUser
   */
  clearUser(): void {
    AuthService.service.clearUser();
  }

  /**
   * getToken
   */
  getToken(): string | null {
    return AuthService.service.getToken();
  }

  /**
   * getUser
   */
  getUser(): ModelUser | null {
    return AuthService.service.getUser();
  }

  /**
   * isLoggedIn
   */
  isLoggedIn(): boolean {
    return AuthService.service.isLoggedIn();
  }

  /**
   * login
   * @param email
   * @param password
   */
  login(email: string, password: string): Promise<ModelUser> {
    return AuthService.service.login(email, password);
  }

  /**
   * ssoProvider
   * @param provider
   * @param redirectRoute
   */
  ssoProvider(provider: AuthSsoProvider, redirectRoute: string) {
    const _ssoProvider = Object.values(AuthSsoProvider);

    if (_ssoProvider.includes(provider as AuthSsoProvider)) {
      let _apiUrl: string | undefined = process.env.REACT_APP_DIA_INTERLINK_API_URL,
        _host: string = `${ window.location.protocol }//${ window.location.host }`,
        _ssoUrl = `${ _apiUrl }/auth/sso/redirect/${ provider }?frontend=${ _host }${ redirectRoute }`;

      window.open(
        _ssoUrl,
        "_self"
      );

      return;
    }

    throw new Error("Provider: '" + provider + "' not found!");
  }

  /**
   * ssoLogin
   * @param token
   * @param state
   * @param message
   */
  async ssoLogin(token: string | null, state: string | null, message: string | null): Promise<ModelUser | null> {
    // check for missing parameters
    if (!token || !state) {
      throw new Error("SSO Login: missing parameters token or state!");
    }

    // success
    if (state === "success") {
      return AuthService.service.loginByToken(token);
    }

    // error
    throw new Error("SSO Error: " + (message || "failed"));
  }

  /**
   * logout
   */
  logout(): void {
    AuthService.service.logout();
  }

  /**
   * passwordForget
   */
  passwordForget(email: string): Promise<any> {
    return AuthService.service.passwordForget(email);
  }

  /**
   * passwordReset
   */
  passwordReset(email: string, password: string, token: string): Promise<any> {
    return AuthService.service.passwordReset(email, password, token);
  }

  /**
   * refreshToken
   */
  refreshToken(): Promise<string> {
    return AuthService.service.refreshToken();
  }

  /**
   * setToken
   * @param accessToken
   */
  setToken(accessToken: string): void {
    AuthService.service.setToken(accessToken);
  }

  /**
   * setUser
   * @param user
   */
  setUser(user: any): void {
    AuthService.service.setUser(user);
  }
}
